import React, { useMemo, useState } from 'react';
import { SwitchTransition } from 'react-transition-group';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useSelector } from 'react-redux';
import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';

import type { RichContent } from '@wix/ricos';

import {
  groupAddCustomTabContent,
  groupCustomTabSave,
} from '@wix/bi-logger-groups/v2';

import {
  selectApplication,
  selectGroupIdBySlug,
  selectHasAdminRole,
  selectIsAppAvailable,
} from 'store/selectors';
import { GroupAppKey } from 'store/groups/types';

import { useRichContent, serialize } from 'common/utils/ricos';
import { useController } from 'common/context/controller';

import { Fade } from 'wui/Fade';
import { Show } from 'wui/Show';
import { Hide } from 'wui/Hide';
import { Card } from 'wui/Card';
import { Button } from 'wui/Button';
import { CardHeader } from 'wui/CardHeader';
import { TextButton } from 'wui/TextButton';
import { EmptyState } from 'wui/EmptyState';

import { RestrictedState } from '../RestrictedState';
import { CustomSourceEditor } from './CustomSourceEditor';
import { CustomSourceViewer } from './CustomSourceViewer';

import { getCustomTabBIData } from './helpers';

export function CustomTabPage() {
  const bi = useBi();
  const router = useRouter();
  const { t } = useTranslation();
  const { params } = useCurrentStateAndParams();
  const { isMobile } = useEnvironment();

  const { group$ } = useController();
  const groupId = useSelector(selectGroupIdBySlug(params.slug));
  const canEdit = useSelector(selectHasAdminRole(groupId));
  const application = useMemo(() => {
    switch (params.tabId) {
      case 'custom_app_2':
        return GroupAppKey.CUSTOM_APP_2;
      case 'custom_app_3':
        return GroupAppKey.CUSTOM_APP_3;
      default:
        return GroupAppKey.CUSTOM_APP;
    }
  }, [params.tabId]);

  const app = useSelector(selectApplication({ groupId, application }));
  const isAppAvailable = useSelector(
    selectIsAppAvailable({
      groupId,
      application,
    }),
  );

  const [isEditMode, setIsEditMode] = useState(false);

  const content = useRichContent(app?.customSource);

  React.useEffect(() => {
    if (!app) {
      router.go('group');
    }
  }, [app]);

  if (!isAppAvailable) {
    return <RestrictedState application={application} />;
  }

  if (!content && !isEditMode) {
    return (
      <EmptyState
        wired
        title={t('groups-web.custom-tab.empty.title')}
        subtitle={t('groups-web.custom-tab.empty.content')}
        action={
          canEdit ? (
            <Button onClick={handleEmptyStateClick}>
              {t('groups-web.custom-tab.empty.button')}
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <RestrictedState application={application}>
      <Card sideBorders={!isMobile}>
        <Show if={canEdit && !isEditMode}>
          <CardHeader
            wired
            action={
              <TextButton wired onClick={handleEditClick}>
                {t('groups-web.custom-tab.edit')}
              </TextButton>
            }
          />
        </Show>

        <Show if={isMobile}>
          <CustomSourceViewer groupId={groupId} content={content} />

          <CustomSourceEditor
            groupId={groupId}
            content={content}
            isOpen={isEditMode}
            onClose={handleCancel}
            onSubmit={handleSubmit}
          />
        </Show>

        <Hide if={isMobile}>
          <SwitchTransition>
            <Fade key={isEditMode ? 'editor' : 'viewer'}>
              {isEditMode ? (
                <CustomSourceEditor
                  groupId={groupId}
                  content={content}
                  onClose={handleCancel}
                  onSubmit={handleSubmit}
                />
              ) : (
                <CustomSourceViewer groupId={groupId} content={content} />
              )}
            </Fade>
          </SwitchTransition>
        </Hide>
      </Card>
    </RestrictedState>
  );

  async function handleSubmit(source?: RichContent) {
    const biData = await getCustomTabBIData(source);

    group$.configureApps(groupId, [
      {
        ...app,
        customSource: serialize(source),
      },
    ]);

    bi.report(
      groupCustomTabSave({
        ...biData,
        groupId,
        tabName: app.customName || t('groups-web.custom'),
      }),
    );
  }

  function handleCancel() {
    setIsEditMode(false);
  }

  function handleEditClick() {
    setIsEditMode(true);

    bi.report(
      groupAddCustomTabContent({
        groupId,
        origin: 'live_site_edit',
        isContentEmpty: 'false',
      }),
    );
  }

  function handleEmptyStateClick() {
    setIsEditMode(true);

    bi.report(
      groupAddCustomTabContent({
        groupId,
        origin: 'live_site_empty_state',
        isContentEmpty: 'true',
      }),
    );
  }
}

CustomTabPage.displayName = 'CustomTabPage';
